import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Gauge, SignOut, CurrencyCircleDollar, WarningOctagon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import GlobalVehicleActivityMonitor from "@/components/GlobalVehicleActivityMonitor";

// Operatörün panik anında bile net görebilmesi için:
// - Her sekme farklı renk (sarı/yeşil/turuncu)
// - Büyük ikon, üstte tek satır BÜYÜK ETİKET, altta küçük tarif
// - Aktif sekme renkli dolu arka plan
const NAV = [
  {
    to: "/operator/home",
    label: "PANEL",
    hint: "Canlı durum",
    icon: Gauge,
    color: {
      active: "bg-primary text-primary-foreground",
      inactive: "text-primary",
      dot: "bg-primary",
    },
  },
  {
    to: "/operator/unpaid",
    label: "ÖDEME AL",
    hint: "Ödenmemiş çıkışlar",
    icon: CurrencyCircleDollar,
    color: {
      active: "bg-green-600 text-white",
      inactive: "text-green-700",
      dot: "bg-green-600",
    },
  },
  {
    to: "/admin/unmatched",
    label: "GİRİŞ YOK",
    hint: "Manuel çıkış girişi",
    icon: WarningOctagon,
    color: {
      active: "bg-amber-500 text-white",
      inactive: "text-amber-700",
      dot: "bg-amber-500",
    },
  },
];

export default function OperatorLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-30 bg-card border-b border-border">
        <div className="px-4 py-3 flex items-center justify-between max-w-2xl mx-auto">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Operatör</div>
            <div className="font-bold" data-testid="operator-user-name">{user?.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="pulse-dot" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Canlı</span>
            <Button variant="ghost" size="icon" onClick={onLogout} data-testid="operator-logout">
              <SignOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {children}
      </main>

      {/* Büyük renkli alt nav — panik anında 1 saniyede anlaşılır */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t-2 border-border shadow-2xl">
        <div className="max-w-2xl mx-auto grid grid-cols-3 gap-1 p-1.5">
          {NAV.map(({ to, label, hint, icon: Icon, color }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`op-nav-${to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 rounded-md py-2.5 px-2 transition-all min-h-[76px] ${
                  isActive
                    ? `${color.active} shadow-md scale-[1.02]`
                    : "bg-muted/40 hover:bg-muted"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={30}
                    weight={isActive ? "fill" : "duotone"}
                    className={isActive ? "" : color.inactive}
                  />
                  <div className="text-center leading-tight">
                    <div className={`text-[13px] font-extrabold tracking-wide ${isActive ? "" : color.inactive}`}>
                      {label}
                    </div>
                    <div className={`text-[9px] uppercase tracking-widest ${
                      isActive ? "opacity-90" : "text-muted-foreground"
                    }`}>
                      {hint}
                    </div>
                  </div>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Global vehicle entry/exit activity popup */}
      <GlobalVehicleActivityMonitor />
    </div>
  );
}
